import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { CsrfService, XSRF_HEADER_NAME } from '../services/csrf.service';
import { GlobalConfig } from '../services/global-config.service';

const PROTECTED_METHODS = new Set(['POST', 'PUT', 'DELETE']);

@Injectable()
export class XsrfInterceptor implements HttpInterceptor {

  constructor(private injector: Injector, private config: GlobalConfig) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!PROTECTED_METHODS.has(request.method.toUpperCase()) || !this.isApiRequest(request.url)) {
      return next.handle(request);
    }

    const csrfService = this.injector.get(CsrfService);

    return csrfService.ensureToken().pipe(
      switchMap(token => next.handle(this.applyToken(request, token)).pipe(
        catchError((error: unknown) => {
          if (!this.isAntiforgeryError(error)) {
            return throwError(() => error);
          }

          return csrfService.fetchToken().pipe(
            switchMap(freshToken => next.handle(this.applyToken(request, freshToken))),
          );
        }),
      )),
    );
  }

  private isApiRequest(url: string): boolean {
    const apiUrl = this.config.apiUrl;

    return !!apiUrl && url.startsWith(apiUrl);
  }

  private applyToken(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    return request.clone({
      withCredentials: true,
      setHeaders: token ? { [XSRF_HEADER_NAME]: token } : {},
    });
  }

  private isAntiforgeryError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || (error.status !== 400 && error.status !== 403)) {
      return false;
    }

    const body: unknown = error.error;
    const text = typeof body === 'string' ? body : body ? JSON.stringify(body) : '';

    if (text) {
      return /antiforgery|xsrf|csrf/i.test(text);
    }

    return error.status === 400;
  }
}
